import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const AIAgentProgressTracker = ({ className = '' }) => {
  const [agents, setAgents] = useState([]);
  const [currentAgentIndex, setCurrentAgentIndex] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const agentWorkflow = [
      { id: 1, name: 'Topic Selection', icon: 'Target', status: 'completed' },
      { id: 2, name: 'Market Research', icon: 'TrendingUp', status: 'completed' },
      { id: 3, name: 'Outline Creation', icon: 'List', status: 'in-progress' },
      { id: 4, name: 'Content Generation', icon: 'FileText', status: 'pending' },
      { id: 5, name: 'Style Refinement', icon: 'Palette', status: 'pending' },
      { id: 6, name: 'Fact Checking', icon: 'CheckCircle', status: 'pending' },
      { id: 7, name: 'SEO Optimization', icon: 'Search', status: 'pending' },
      { id: 8, name: 'Cover Design', icon: 'Image', status: 'pending' },
      { id: 9, name: 'Formatting', icon: 'Layout', status: 'pending' },
      { id: 10, name: 'Quality Review', icon: 'Shield', status: 'pending' },
      { id: 11, name: 'Publishing Prep', icon: 'Send', status: 'pending' }
    ];

    setAgents(agentWorkflow);
    
    const inProgressIndex = agentWorkflow?.findIndex(agent => agent?.status === 'in-progress');
    setCurrentAgentIndex(inProgressIndex !== -1 ? inProgressIndex : 0);
  }, []);

  const handleAgentClick = (agent, index) => {
    if (agent?.status === 'completed' || agent?.status === 'in-progress') {
      setCurrentAgentIndex(index);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'completed': 'bg-success text-success-foreground',
      'in-progress': 'bg-primary text-primary-foreground',
      'pending': 'bg-muted text-muted-foreground'
    };
    return colors?.[status] || colors?.pending;
  };

  const getStatusIcon = (status) => {
    if (status === 'completed') return 'Check';
    if (status === 'in-progress') return 'Loader';
    return 'Circle';
  };

  const completedCount = agents?.filter(a => a?.status === 'completed')?.length;
  const progressPercentage = (completedCount / agents?.length) * 100;

  return (
    <div className={`bg-card border border-border rounded-lg shadow-sm ${className}`}>
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-heading font-semibold text-foreground">
            AI Agent Workflow
          </h3>
          <span className="text-xs font-mono text-muted-foreground">
            {completedCount}/{agents?.length} Complete
          </span>
        </div>

        <div className="relative w-full h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="absolute top-0 left-0 h-full bg-primary transition-all duration-400 ease-in-out"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>
      <div className="p-2 max-h-96 overflow-y-auto">
        <div className="space-y-1">
          {agents?.map((agent, index) => {
            const isAccessible = agent?.status === 'completed' || agent?.status === 'in-progress';
            const isCurrent = index === currentAgentIndex;

            return (
              <button
                key={agent?.id}
                onClick={() => handleAgentClick(agent, index)}
                disabled={!isAccessible}
                className={`w-full p-3 rounded-md flex items-center space-x-3 transition-all duration-200 ${
                  isCurrent
                    ? 'bg-primary/10 border border-primary'
                    : isAccessible
                    ? 'hover:bg-muted border border-transparent' :'opacity-50 cursor-not-allowed border border-transparent'
                }`}
              >
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${getStatusColor(agent?.status)}`}>
                  <Icon 
                    name={getStatusIcon(agent?.status)} 
                    size={16}
                    className={agent?.status === 'in-progress' ? 'animate-spin' : ''}
                  />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <div className="flex items-center space-x-2">
                    <Icon 
                      name={agent?.icon} 
                      size={16} 
                      className="flex-shrink-0 text-muted-foreground"
                    />
                    <span className={`text-sm font-medium truncate ${
                      isCurrent ? 'text-primary' : 'text-foreground'
                    }`}>
                      {agent?.name}
                    </span>
                  </div>
                </div>
                {isAccessible && (
                  <Icon 
                    name="ChevronRight" 
                    size={16} 
                    className="flex-shrink-0 text-muted-foreground"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentAgentIndex(Math.max(0, currentAgentIndex - 1))}
            disabled={currentAgentIndex === 0}
            className="flex items-center space-x-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <Icon name="ChevronLeft" size={14} />
            <span>Previous</span>
          </button>

          <span className="text-xs font-mono text-muted-foreground">
            Step {currentAgentIndex + 1} of {agents?.length}
          </span>

          <button
            onClick={() => setCurrentAgentIndex(Math.min(agents?.length - 1, currentAgentIndex + 1))}
            disabled={currentAgentIndex === agents?.length - 1 || agents?.[currentAgentIndex + 1]?.status === 'pending'}
            className="flex items-center space-x-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <span>Next</span>
            <Icon name="ChevronRight" size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIAgentProgressTracker;